import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useRFQVendorInvitations } from '@/hooks/useRFQVendorInvitations';
import { VendorSelectionModal } from './VendorSelectionModal';
import { 
  Users, 
  Plus, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Building,
  MapPin,
  Star,
  Shield,
  MoreVertical,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';

interface VendorInvitationManagerProps {
  rfqId?: string;
  isEditable?: boolean;
  selectedVendors: any[];
  onVendorsChange: (vendors: any[]) => void;
  rfqCategory?: string;
  deliveryLocation?: { latitude: number; longitude: number };
}

export function VendorInvitationManager({ 
  rfqId, 
  isEditable = true, 
  selectedVendors,
  onVendorsChange,
  rfqCategory,
  deliveryLocation 
}: VendorInvitationManagerProps) {
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [invitationNotes, setInvitationNotes] = useState('');
  
  const { 
    invitations, 
    loading, 
    inviteVendors, 
    resendInvitation, 
    removeInvitation 
  } = useRFQVendorInvitations(rfqId);

  const handleVendorsSelected = (vendors: any[]) => {
    onVendorsChange(vendors);
  };

  const handleSendInvitations = async () => {
    if (!rfqId || selectedVendors.length === 0) return;

    try {
      await inviteVendors({
        rfq_id: rfqId,
        vendor_user_ids: selectedVendors.map(v => v.user_id),
        invitation_notes: invitationNotes || undefined,
      });
      setInvitationNotes('');
    } catch (error) {
      console.error('Failed to send invitations:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'invited':
        return <Mail className="h-4 w-4 text-blue-500" />;
      case 'viewed':
        return <Eye className="h-4 w-4 text-green-500" />;
      case 'declined':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'bid_submitted':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'expired':
        return <Clock className="h-4 w-4 text-gray-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'invited':
        return 'Invited';
      case 'viewed':
        return 'Viewed';
      case 'declined':
        return 'Declined';
      case 'bid_submitted':
        return 'Bid Submitted';
      case 'expired':
        return 'Expired';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'invited':
        return 'bg-blue-500';
      case 'viewed':
        return 'bg-green-500';
      case 'declined':
        return 'bg-red-500';
      case 'bid_submitted':
        return 'bg-green-600';
      case 'expired':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getVerificationBadge = (vendor: any) => {
    if (!vendor.is_verified) return null;
    
    const level = vendor.verification_level;
    const colors = {
      basic: 'bg-blue-500',
      enhanced: 'bg-green-500',
      premium: 'bg-purple-500'
    };
    
    return (
      <Badge 
        variant="secondary" 
        className={`${colors[level as keyof typeof colors] || 'bg-gray-500'} text-white text-xs`}
      >
        <Shield className="h-3 w-3 mr-1" />
        {level?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Vendor Invitations
            {(selectedVendors.length > 0 || invitations.length > 0) && (
              <Badge variant="secondary">
                {rfqId ? invitations.length : selectedVendors.length} vendor{(rfqId ? invitations.length : selectedVendors.length) !== 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        
        {/* Before RFQ is published - show selected vendors */}
        {!rfqId && selectedVendors.length > 0 && (
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedVendors.map((vendor) => (
                  <div key={vendor.id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        <Building className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{vendor.organization_name || vendor.business_name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {getVerificationBadge(vendor)}
                        {vendor.average_rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs">{vendor.average_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {isEditable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updatedVendors = selectedVendors.filter(v => v.id !== vendor.id);
                          onVendorsChange(updatedVendors);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Invitation Notes */}
              {isEditable && (
                <div className="space-y-2">
                  <Label htmlFor="invitation-notes">Optional Message for Vendors</Label>
                  <Textarea
                    id="invitation-notes"
                    placeholder="Add a personal message to include with the invitations..."
                    value={invitationNotes}
                    onChange={(e) => setInvitationNotes(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    This message will be included in the invitation emails sent to vendors.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        )}

        {/* After RFQ is published - show invitations with status */}
        {rfqId && invitations.length > 0 && (
          <CardContent>
            <div className="space-y-4">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback>
                      <Building className="h-6 w-6" />
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium truncate">
                        {invitation.vendor_profile?.organization_name || invitation.vendor_profile?.business_name || 'Unknown Vendor'}
                      </h4>
                      {getVerificationBadge(invitation.vendor_profile)}
                    </div>
                    
                    {invitation.vendor_profile?.headquarters_country && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {invitation.vendor_profile.headquarters_state && 
                          `${invitation.vendor_profile.headquarters_state}, `}
                        {invitation.vendor_profile.headquarters_country}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="secondary" 
                      className={`${getStatusColor(invitation.invitation_status)} text-white`}
                    >
                      {getStatusIcon(invitation.invitation_status)}
                      <span className="ml-1">{getStatusLabel(invitation.invitation_status)}</span>
                    </Badge>

                    {invitation.vendor_profile?.average_rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-medium">
                          {invitation.vendor_profile.average_rating.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>

                  {isEditable && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {invitation.invitation_status === 'invited' && (
                          <DropdownMenuItem onClick={() => resendInvitation(invitation.id)}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Resend Invitation
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => removeInvitation(invitation.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove Invitation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}

              {/* Send Invitations Button */}
              {!rfqId && selectedVendors.length > 0 && isEditable && (
                <>
                  <Separator />
                  <div className="flex justify-end">
                    <Button onClick={handleSendInvitations} disabled={loading}>
                      <Mail className="h-4 w-4 mr-2" />
                      {loading ? 'Sending...' : `Send Invitations (${selectedVendors.length})`}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        )}

        {/* Empty State */}
        {!rfqId && selectedVendors.length === 0 && (
          <CardContent className="pt-0">
            <p className="text-muted-foreground mb-4">
              Select vendors to invite to this private RFQ. We recommend inviting at least 3 vendors for competitive bidding.
            </p>
            {isEditable && (
              <Button onClick={() => setShowVendorModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Select Vendors
              </Button>
            )}
          </CardContent>
        )}

        {rfqId && invitations.length === 0 && (
          <CardContent className="text-center py-8">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Invitations Sent</h3>
            <p className="text-muted-foreground">
              Invitations will appear here once vendors are invited to this RFQ.
            </p>
          </CardContent>
        )}
      </Card>

      {/* Vendor Selection Modal */}
      <VendorSelectionModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        onVendorsSelected={handleVendorsSelected}
        selectedVendors={selectedVendors}
        rfqCategory={rfqCategory}
        deliveryLocation={deliveryLocation}
      />
    </div>
  );
}